import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { api, formatCurrency } from '../../utils/api'
import { PRODUCT_TYPES } from '../../utils/constants'
import { MetricsRow, MultiSelectDropdown, ProgressBar, SearchLinks } from '../shared'

function Repricing() {
  const [allItems, setAllItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [error, setError] = useState(null)
  const [collections, setCollections] = useState([])
  const [saleCollectionId, setSaleCollectionId] = useState(null)
  const [selectedItems, setSelectedItems] = useState(new Set())
  const [processing, setProcessing] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0, status: '' })
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const abortControllerRef = useRef(null)

  // Search filters (trigger API call)
  const [searchFilters, setSearchFilters] = useState({
    product_types: [],
    exclude_repriced_days: '',
    repriced_within_days: '',
    created_from: '',
    created_to: '',
    product_ids: '',
    price_min: '',
    price_max: ''
  })

  // Display options (client-side)
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState('asc')
  const [limit, setLimit] = useState(100)

  // Quick filter selection (just for highlighting, actual values are in searchFilters)
  const [selectedQuickFilter, setSelectedQuickFilter] = useState(null)

  // Pricing method: 'percentage', 'manual', 'ai'
  const [pricingMethod, setPricingMethod] = useState('percentage')
  const [pricingSettings, setPricingSettings] = useState({
    percentage: 20,
    add_to_sale: true
  })
  // Manual price edits: { productId: newPrice }
  const [manualPrices, setManualPrices] = useState({})
  // AI pricing results: { productId: { sellPrice, error } }
  const [aiPrices, setAiPrices] = useState({})
  const [aiPricing, setAiPricing] = useState(false)

  useEffect(() => {
    loadCollections()
    // Don't load items on initial load - wait for user to search
  }, [])

  async function loadCollections() {
    try {
      const response = await api.get('/api/inventory/shopify/collections')
      const colls = response.collections || []
      setCollections(colls)
      const saleCollection = colls.find(c => c.title.toUpperCase() === 'SALE')
      if (saleCollection) setSaleCollectionId(saleCollection.id)
    } catch (err) {
      console.error('Failed to load collections:', err)
    }
  }

  const loadItems = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      params.set('stock_status', 'in_stock')
      params.set('status', 'active')
      if (searchFilters.product_types.length > 0) params.set('product_types', searchFilters.product_types.join(','))
      if (searchFilters.price_min) params.set('price_min', searchFilters.price_min)
      if (searchFilters.price_max) params.set('price_max', searchFilters.price_max)
      if (searchFilters.created_from) params.set('created_from', searchFilters.created_from)
      if (searchFilters.created_to) params.set('created_to', searchFilters.created_to)
      if (searchFilters.exclude_repriced_days) params.set('exclude_repriced_days', searchFilters.exclude_repriced_days)
      if (searchFilters.repriced_within_days) params.set('repriced_within_days', searchFilters.repriced_within_days)
      if (searchFilters.product_ids) params.set('product_ids', searchFilters.product_ids)
      params.set('limit', '500')

      const response = await api.get(`/api/inventory/shopify/search?${params}`)
      setAllItems(response.products || [])
      setTotalCount(response.count || 0)
      setSelectedItems(new Set())
      setManualPrices({})
      setAiPrices({})
    } catch (err) {
      console.error('Failed to load items:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [searchFilters])

  function handleFilterChange(field, value) {
    setSearchFilters(prev => ({ ...prev, [field]: value }))
  }

  function handleSearch(e) {
    e.preventDefault()
    setHasSearched(true)
    loadItems()
  }

  function clearFilters() {
    setSearchFilters({
      product_types: [],
      exclude_repriced_days: '',
      repriced_within_days: '',
      created_from: '',
      created_to: '',
      product_ids: '',
      price_min: '',
      price_max: ''
    })
    setSelectedQuickFilter(null)
    setSortBy('updated_at')
    setSortOrder('desc')
    setLimit(100)
  }

  function setQuickAgeFilter(months) {
    const date = new Date()
    date.setMonth(date.getMonth() - months)
    // Just update filters, don't trigger search
    setSearchFilters(prev => ({
      ...prev,
      created_to: date.toISOString().split('T')[0],
      created_from: ''
    }))
    setSelectedQuickFilter(months)
  }

  // Client-side sorting and pagination
  const displayItems = useMemo(() => {
    const sorted = [...allItems].sort((a, b) => {
      let aVal = a[sortBy]
      let bVal = b[sortBy]

      if (['price', 'cost', 'quantity', 'compare_at_price'].includes(sortBy)) {
        aVal = parseFloat(aVal) || 0
        bVal = parseFloat(bVal) || 0
      }

      if (typeof aVal === 'string') aVal = aVal.toLowerCase()
      if (typeof bVal === 'string') bVal = bVal.toLowerCase()

      if (['created_at', 'updated_at'].includes(sortBy)) {
        aVal = new Date(aVal).getTime()
        bVal = new Date(bVal).getTime()
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    return sorted.slice(0, limit)
  }, [allItems, sortBy, sortOrder, limit])

  function handleSort(field) {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  function SortHeader({ field, children, align }) {
    const isActive = sortBy === field
    const arrow = isActive ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : ''
    return (
      <th
        onClick={() => handleSort(field)}
        style={{ cursor: 'pointer', textAlign: align, userSelect: 'none' }}
        className={isActive ? 'sort-active' : ''}
      >
        {children}{arrow}
      </th>
    )
  }

  function handleSelectAll(e) {
    if (e.target.checked) {
      setSelectedItems(new Set(displayItems.map(item => item.id)))
    } else {
      setSelectedItems(new Set())
    }
  }

  function handleSelectItem(id) {
    setSelectedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) newSet.delete(id)
      else newSet.add(id)
      return newSet
    })
  }

  function handleManualPriceChange(productId, value) {
    setManualPrices(prev => ({
      ...prev,
      [productId]: value
    }))
  }

  function getNewPrice(item) {
    if (pricingMethod === 'manual') {
      const manualPrice = manualPrices[item.id]
      return manualPrice !== undefined ? parseFloat(manualPrice) || 0 : parseFloat(item.price) || 0
    }
    const currentPrice = parseFloat(item.price) || 0
    return currentPrice * (1 - pricingSettings.percentage / 100)
  }

  function isInSaleCollection(item) {
    if (!saleCollectionId || !item.collections) return false
    return item.collections.some(c => c.id === saleCollectionId || c.id === String(saleCollectionId))
  }

  async function handleAiPriceSelected() {
    if (selectedItems.size === 0) return

    setAiPricing(true)
    setAiPrices({})
    abortControllerRef.current = new AbortController()
    const ids = Array.from(selectedItems)
    const results = {}

    for (let i = 0; i < ids.length; i++) {
      if (abortControllerRef.current.signal.aborted) break
      const id = ids[i]
      const item = allItems.find(it => it.id === id)
      const title = item?.title || `Product ${id}`

      setProgress({
        current: i + 1,
        total: ids.length,
        status: `AI pricing: ${title.substring(0, 40)}...`
      })

      try {
        const result = await api.post(`/api/inventory/shopify/price-draft/${id}?dry_run=true`)
        results[id] = { sellPrice: result.sellPrice || 0, error: false }
      } catch (err) {
        results[id] = { sellPrice: null, error: true }
      }
    }

    setAiPrices(results)
    setAiPricing(false)
    setProgress({ current: 0, total: 0, status: '' })
  }

  async function handleRepriceSelected() {
    if (selectedItems.size === 0) return

    const confirmed = window.confirm(
      `Are you sure you want to reprice ${selectedItems.size} products?\n\n` +
      (pricingMethod === 'percentage' ? `This will reduce prices by ${pricingSettings.percentage}%.` :
       pricingMethod === 'ai' ? 'This will apply the AI-calculated prices.' :
       'This will apply your manual price changes.') +
      (pricingSettings.add_to_sale ? '\n\nProducts will also be added to the SALE collection.' : '')
    )
    if (!confirmed) return

    setProcessing(true)
    abortControllerRef.current = new AbortController()
    const ids = Array.from(selectedItems)
    let completed = 0
    let errors = []

    for (const id of ids) {
      if (abortControllerRef.current.signal.aborted) break

      const item = allItems.find(i => i.id === id)
      if (!item) continue

      setProgress({
        current: completed + 1,
        total: ids.length,
        status: `Repricing ${completed + 1} of ${ids.length}...`
      })

      try {
        const payload = {
          dry_run: false,
          add_to_sale: pricingSettings.add_to_sale,
          sale_collection_id: saleCollectionId
        }

        if (pricingMethod === 'percentage') {
          payload.method = 'percentage'
          payload.percentage = pricingSettings.percentage
        } else if (pricingMethod === 'ai') {
          const aiResult = aiPrices[id]
          if (!aiResult || aiResult.error || !aiResult.sellPrice) {
            errors.push({ id, error: 'No AI price available' })
            completed++
            continue
          }
          payload.method = 'fixed'
          payload.new_price = aiResult.sellPrice
        } else {
          payload.method = 'fixed'
          payload.new_price = manualPrices[id] || item.price
        }

        await api.post(`/api/inventory/shopify/price-product/${id}`, payload)
        completed++
      } catch (err) {
        errors.push({ id, error: err.message })
        completed++
      }
    }

    setProcessing(false)
    setProgress({ current: 0, total: 0, status: '' })

    if (errors.length > 0) {
      alert(`Completed with ${errors.length} errors. ${completed - errors.length} products repriced successfully.`)
    } else {
      alert(`Successfully repriced ${completed} products.`)
    }

    loadItems()
  }

  function handleCancelOperation() {
    if (abortControllerRef.current) abortControllerRef.current.abort()
  }

  // Check if advanced filters are active
  const hasAdvancedFilters = searchFilters.product_types.length > 0 ||
    searchFilters.price_min || searchFilters.price_max ||
    searchFilters.created_from || searchFilters.created_to || searchFilters.product_ids

  // Calculate metrics
  const totalValue = displayItems.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0)
  const totalCost = displayItems.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0)
  const avgPrice = displayItems.length > 0 ? totalValue / displayItems.length : 0

  const metrics = [
    { label: 'Loaded', value: allItems.length, color: 'blue' },
    { label: 'Total Value', value: totalValue, format: 'currency', color: 'purple' },
    { label: 'Average Price', value: avgPrice, format: 'currency', color: 'amber' },
    { label: 'Total Cost', value: totalCost, format: 'currency', color: 'green' }
  ]

  const excludeRepricedOptions = [
    { value: '', label: 'Show all' },
    { value: '7', label: '7 days' },
    { value: '14', label: '14 days' },
    { value: '30', label: '30 days' },
    { value: '60', label: '60 days' },
    { value: '90', label: '90 days' }
  ]

  const repricedWithinOptions = [
    { value: '', label: 'Show all' },
    { value: '7', label: '7 days' },
    { value: '14', label: '14 days' },
    { value: '30', label: '30 days' },
    { value: '60', label: '60 days' },
    { value: '90', label: '90 days' }
  ]

  const productTypeOptions = PRODUCT_TYPES.map(t => ({ value: t, label: t }))

  return (
    <div>
      <MetricsRow metrics={metrics} />

      {(processing || aiPricing) && (
        <ProgressBar
          current={progress.current}
          total={progress.total}
          status={progress.status}
          onCancel={handleCancelOperation}
        />
      )}

      {/* Quick Age Filters */}
      <div className="quick-links">
        <span className="text-sm text-muted">Quick filters:</span>
        <button
          type="button"
          className={`quick-link ${selectedQuickFilter === 6 ? 'quick-link-active' : ''}`}
          onClick={() => setQuickAgeFilter(6)}
        >
          Over 6 months old
        </button>
        <button
          type="button"
          className={`quick-link ${selectedQuickFilter === 12 ? 'quick-link-active' : ''}`}
          onClick={() => setQuickAgeFilter(12)}
        >
          Over 1 year old
        </button>
        <button
          type="button"
          className={`quick-link ${selectedQuickFilter === 24 ? 'quick-link-active' : ''}`}
          onClick={() => setQuickAgeFilter(24)}
        >
          Over 2 years old
        </button>
      </div>

      <hr className="section-divider" />

      <form onSubmit={handleSearch}>
        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
          {/* Main Filters */}
          <div className="filter-section">
            <div className="filter-group">
              <label className="filter-label">Exclude repriced</label>
              <select
                className="form-input"
                value={searchFilters.exclude_repriced_days}
                onChange={e => handleFilterChange('exclude_repriced_days', e.target.value)}
              >
                {excludeRepricedOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">Repriced within</label>
              <select
                className="form-input"
                value={searchFilters.repriced_within_days}
                onChange={e => handleFilterChange('repriced_within_days', e.target.value)}
              >
                {repricedWithinOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">Product Types</label>
              <MultiSelectDropdown
                options={productTypeOptions}
                selected={searchFilters.product_types}
                onChange={values => handleFilterChange('product_types', values)}
                placeholder="All types"
              />
            </div>
          </div>

          {/* Advanced Toggle */}
          <button
            type="button"
            className="filter-toggle"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <span className={`filter-toggle-icon ${showAdvanced ? 'expanded' : ''}`}>▼</span>
            {showAdvanced ? 'Hide' : 'Show'} Advanced Search
            {hasAdvancedFilters && <span className="filter-toggle-active">(filters active)</span>}
          </button>

          {/* Advanced Filters */}
          {showAdvanced && (
            <div className="advanced-filters">
              <div className="filter-section">
                <div className="filter-group">
                  <label className="filter-label">Created From</label>
                  <input
                    type="date"
                    className="form-input"
                    value={searchFilters.created_from}
                    onChange={e => handleFilterChange('created_from', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">Created To</label>
                  <input
                    type="date"
                    className="form-input"
                    value={searchFilters.created_to}
                    onChange={e => handleFilterChange('created_to', e.target.value)}
                  />
                </div>
                <div className="filter-group filter-group-wide">
                  <label className="filter-label">Product IDs (comma-separated)</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="123456, 789012"
                    value={searchFilters.product_ids}
                    onChange={e => handleFilterChange('product_ids', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">Price Min (£)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="0.00"
                    value={searchFilters.price_min}
                    onChange={e => handleFilterChange('price_min', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">Price Max (£)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="9999.00"
                    value={searchFilters.price_max}
                    onChange={e => handleFilterChange('price_max', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="filter-actions" style={{ justifyContent: 'flex-start', gap: 'var(--space-3)' }}>
            <button type="button" className="btn btn-secondary" onClick={clearFilters}>
              Clear
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Loading...' : 'Fetch Products'}
            </button>
            {allItems.length > 0 && (
              <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>
                Showing {displayItems.length} of {allItems.length} loaded ({totalCount} total)
              </span>
            )}
          </div>
        </div>
      </form>

      {/* Pricing Controls */}
      {displayItems.length > 0 && (
        <div className="card" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
            {/* Pricing Method Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span className="text-sm" style={{ fontWeight: 500 }}>Pricing Method:</span>
              <div style={{ display: 'flex', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--color-border)' }}>
                <button
                  type="button"
                  className={`btn btn-sm ${pricingMethod === 'percentage' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ borderRadius: 0, border: 'none' }}
                  onClick={() => setPricingMethod('percentage')}
                >
                  Percentage Off
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${pricingMethod === 'manual' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ borderRadius: 0, border: 'none', borderLeft: '1px solid var(--color-border)' }}
                  onClick={() => setPricingMethod('manual')}
                >
                  Manual
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${pricingMethod === 'ai' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ borderRadius: 0, border: 'none', borderLeft: '1px solid var(--color-border)' }}
                  onClick={() => setPricingMethod('ai')}
                >
                  AI Pricing
                </button>
              </div>
            </div>

            {/* Percentage input - only show for percentage method */}
            {pricingMethod === 'percentage' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <input
                  type="number"
                  className="form-input"
                  style={{ width: 70 }}
                  value={pricingSettings.percentage}
                  onChange={e => setPricingSettings(prev => ({ ...prev, percentage: parseFloat(e.target.value) || 0 }))}
                  min="0"
                  max="100"
                  step="1"
                />
                <span className="text-sm">% off</span>
              </div>
            )}

            {/* Add to SALE checkbox */}
            {saleCollectionId && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <input
                  type="checkbox"
                  checked={pricingSettings.add_to_sale}
                  onChange={e => setPricingSettings(prev => ({ ...prev, add_to_sale: e.target.checked }))}
                />
                <span className="text-sm">Add to SALE</span>
              </label>
            )}

            {/* Selected count */}
            <span className="text-sm" style={{ fontWeight: 500, color: 'var(--color-primary)' }}>
              {selectedItems.size} selected
            </span>

            {/* Action buttons */}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--space-2)' }}>
              {pricingMethod === 'ai' && (
                <button
                  className="btn btn-secondary"
                  onClick={handleAiPriceSelected}
                  disabled={selectedItems.size === 0 || aiPricing || processing}
                >
                  {aiPricing ? 'Calculating...' : `Get AI Prices (${selectedItems.size})`}
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={handleRepriceSelected}
                disabled={selectedItems.size === 0 || processing || aiPricing || (pricingMethod === 'ai' && Object.keys(aiPrices).length === 0)}
              >
                {pricingMethod === 'ai' ? `Apply AI Prices (${selectedItems.size})` : `Reprice Selected (${selectedItems.size})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="card">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : !hasSearched ? (
          <div className="empty-state">
            <div className="empty-state-title">Search for Products</div>
            <div className="empty-state-description">Use the filters above and click "Fetch Products" to load items for repricing.</div>
          </div>
        ) : displayItems.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Products Found</div>
            <div className="empty-state-description">Try adjusting your search filters.</div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={selectedItems.size === displayItems.length && displayItems.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <SortHeader field="title">Title</SortHeader>
                <SortHeader field="product_type">Category</SortHeader>
                <SortHeader field="quantity" align="right">Qty</SortHeader>
                <SortHeader field="cost" align="right">Cost</SortHeader>
                <SortHeader field="price" align="right">Price</SortHeader>
                <th style={{ textAlign: 'right' }}>Compare At</th>
                {pricingMethod === 'percentage' && (
                  <th style={{ textAlign: 'right', color: 'var(--color-success)' }}>
                    -{pricingSettings.percentage}%
                  </th>
                )}
                {pricingMethod === 'manual' && (
                  <th style={{ textAlign: 'right' }}>New Price</th>
                )}
                {pricingMethod === 'ai' && (
                  <th style={{ textAlign: 'right', color: 'var(--color-success)' }}>AI Price</th>
                )}
                <th>Last Repriced</th>
                <SortHeader field="created_at">Created</SortHeader>
                <th style={{ width: 120 }}>Search</th>
              </tr>
            </thead>
            <tbody>
              {displayItems.map(item => {
                const currentPrice = parseFloat(item.price) || 0
                const newPrice = getNewPrice(item)
                const inSale = isInSaleCollection(item)
                return (
                  <tr key={item.id} className={selectedItems.has(item.id) ? 'row-selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.id)}
                        onChange={() => handleSelectItem(item.id)}
                      />
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <div style={{ fontWeight: 500, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <a href={`/inventory/list?productId=${item.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                            {item.title}
                          </a>
                        </div>
                        {inSale && (
                          <span style={{
                            background: 'var(--color-error)',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 10,
                            fontWeight: 600,
                            textTransform: 'uppercase'
                          }}>SALE</span>
                        )}
                      </div>
                    </td>
                    <td className="text-sm">{item.product_type || '—'}</td>
                    <td style={{ textAlign: 'right' }} className="text-sm">{item.quantity ?? item.inventory_quantity ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.cost)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 500 }}>{formatCurrency(item.price)}</td>
                    <td style={{ textAlign: 'right' }} className="text-muted">
                      {item.compare_at_price ? formatCurrency(item.compare_at_price) : '—'}
                    </td>
                    {pricingMethod === 'manual' ? (
                      <td style={{ textAlign: 'right' }}>
                        <input
                          type="number"
                          className="form-input"
                          style={{ width: 80, textAlign: 'right' }}
                          placeholder={currentPrice.toFixed(2)}
                          value={manualPrices[item.id] ?? ''}
                          onChange={e => handleManualPriceChange(item.id, e.target.value)}
                          step="0.01"
                        />
                      </td>
                    ) : pricingMethod === 'ai' ? (
                      <td style={{ textAlign: 'right' }}>
                        {aiPrices[item.id] ? (
                          aiPrices[item.id].error ? (
                            <span className="text-muted">Error</span>
                          ) : aiPrices[item.id].sellPrice ? (
                            <span style={{ color: aiPrices[item.id].sellPrice < currentPrice ? 'var(--color-success)' : 'var(--color-text)', fontWeight: 500 }}>
                              {formatCurrency(aiPrices[item.id].sellPrice)}
                            </span>
                          ) : (
                            <span className="text-muted">No data</span>
                          )
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    ) : (
                      <td style={{ textAlign: 'right', color: newPrice < currentPrice ? 'var(--color-success)' : 'var(--color-text)' }}>
                        {formatCurrency(newPrice)}
                      </td>
                    )}
                    <td className="text-sm text-muted">
                      {item.last_repriced_at ? new Date(item.last_repriced_at).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td className="text-sm text-muted">{new Date(item.created_at).toLocaleDateString('en-GB')}</td>
                    <td><SearchLinks title={item.title} /></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {/* Per-page selector */}
        {displayItems.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
            <span className="text-sm text-muted">
              Showing {displayItems.length} of {allItems.length} loaded
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span className="text-sm text-muted">Per page:</span>
              {[25, 50, 100, 200].map(num => (
                <button
                  key={num}
                  type="button"
                  className={`btn btn-sm ${limit === num ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setLimit(num)}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

    </div>
  )
}

export default Repricing
